import {
	Checkbox,
	Chip,
	Stack,
	styled,
	TableCell,
	TableRow,
} from "@mui/material";
import { green } from "@mui/material/colors";
import { types } from "api";
import { ReactComponent as CrownIcon } from "assets/icons/crown.svg";
import clsx from "clsx";
import { StyledButton } from "components/StyledButton";
import React from "react";
import { Link } from "react-router-dom";
import { getPath, routes } from "routes";

export interface ViewGroupsPageTableRowProps {
	selected: string[];
	onCheckboxClick: (event: React.MouseEvent<unknown>, userid: string) => void;
	onEditClick?: (userid: string) => void;
	row: types.User;
	labelId: string;
	isMe: boolean;
}

const PREFIX = "RegisteredTableRow";

const classes = {
	admin: `${PREFIX}-admin`,
	teacher: `${PREFIX}-teacher`,
	student: `${PREFIX}-student`,
	parent: `${PREFIX}-parent`,
};

const CustomChip = styled("div")(({ theme }) => ({
	[`& .${classes.admin}`]: {
		backgroundColor: theme.palette.secondary.main,
		color: theme.palette.background.paper,
		marginRight: theme.spacing(1),
	},
	[`& .${classes.teacher}`]: {
		backgroundColor: theme.palette.secondary.light,
		color: theme.palette.background.paper,
		marginRight: theme.spacing(1),
	},
	[`& .${classes.student}`]: {
		backgroundColor: green[400],
		color: theme.palette.background.paper,
		marginRight: theme.spacing(1),
	},
	[`& .${classes.parent}`]: {
		backgroundColor: theme.palette.secondary.dark,
		color: theme.palette.background.paper,
		marginRight: theme.spacing(1),
	},
}));

export const RegisteredTableRow = ({
	selected,
	row,
	onCheckboxClick,
	onEditClick,
	labelId,
	isMe,
}: ViewGroupsPageTableRowProps): JSX.Element => {
	const isSelected = (userid: string): boolean =>
		selected.indexOf(userid) !== -1;

	const isItemSelected = isSelected(row.userId);

	const buttonProps = onEditClick
		? {
				onClick: (): void => {
					onEditClick(row.userId);
				},
		  }
		: {
				component: Link,
				to: isMe
					? routes._.people._.me.path
					: getPath(routes._.people._.userProfile, {
							userId: row.userId,
					  }),
		  };

	return (
		<TableRow
			hover
			role="checkbox"
			aria-checked={isItemSelected}
			key={row.email}
			selected={isItemSelected}
		>
			<TableCell padding="checkbox">
				<Checkbox
					checked={isItemSelected}
					data-testid="checkbox"
					inputProps={{ "aria-labelledby": labelId }}
					onClick={(event) => onCheckboxClick(event, row.userId)}
					disabled={isMe}
				/>
			</TableCell>
			<TableCell padding="none" data-testid="firstName">
				<Stack direction="row" alignItems="center">
					{row.firstName}
					{isMe && <CrownIcon style={{ height: "2rem", width: "2rem" }} />}
				</Stack>
			</TableCell>
			<TableCell padding="none" data-testid="lastName">
				{row.lastName}
			</TableCell>
			<TableCell padding="none" data-testid="email">
				<CustomChip>
					{row.role.map((role, index) => (
						<Chip
							className={clsx({
								[classes.admin]: role === "admin",
								[classes.teacher]: role === "teacher",
								[classes.student]: role === "student",
								[classes.parent]: role === "parent",
							})}
							key={index}
							label={role}
						/>
					))}
				</CustomChip>
			</TableCell>
			<TableCell padding="none" data-testid="email">
				{row.email}
			</TableCell>
			<TableCell padding="none" data-testid="edit-action">
				<StyledButton color="primary" {...buttonProps}>
					Edit Profile
				</StyledButton>
			</TableCell>
		</TableRow>
	);
};
